import axios from 'axios';
import authService from './authService';
import { API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests (appraiser or homeowner)
api.interceptors.request.use(
  (config) => {
    let token = null;
    
    // For upload endpoint, use homeowner token (only homeowners upload photos)
    if (config.url?.includes('/upload')) {
      token = localStorage.getItem('homeowner_token');
    }
    
    // For other endpoints, check appraiser token first, then homeowner token
    if (!token) {
      token = authService.getToken();
    }
    if (!token) {
      token = localStorage.getItem('homeowner_token');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses by clearing auth
// BUT don't intercept login/signup errors - let those components handle them
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/api/auth/login') || 
                          error.config?.url?.includes('/api/auth/signup') ||
                          error.config?.url?.includes('/api/sessions/verify-code');
    
    // Only auto-logout/redirect for 401s that aren't from login/signup attempts
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Check if this is a homeowner (has homeowner_token)
      const homeownerToken = localStorage.getItem('homeowner_token');
      if (homeownerToken) {
        // Clear homeowner token and stay on same page (they can re-verify)
        localStorage.removeItem('homeowner_token');
      } else {
        // This is an appraiser - logout and redirect to login
        authService.logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const createSession = async (orderId) => {
  const response = await api.post('/api/sessions', { orderId });
  return response.data;
};

export const verifyAccessCode = async (sessionId, accessCode) => {
  const response = await api.post('/api/sessions/verify-code', { sessionId, accessCode });
  return response.data;
};

export const getActiveSession = async () => {
  const response = await api.get('/api/sessions/active');
  return response.data;
};

export const closeSession = async (sessionId) => {
  const response = await api.post(`/api/sessions/${sessionId}/close`);
  return response.data;
};

export const getSessionHistory = async () => {
  const response = await api.get('/api/sessions/history');
  return response.data;
};

export const getSessionImages = async (sessionId) => {
  const response = await api.get(`/api/sessions/${sessionId}/images`);
  return response.data;
};

export const deleteImage = async (sessionId, fileName) => {
  const response = await api.delete(`/api/sessions/${sessionId}/images/${encodeURIComponent(fileName)}`);
  return response.data;
};

export const sendSessionInvite = async (sessionId, email, customMessage = null) => {
  const response = await api.post(`/api/sessions/${sessionId}/send-invite`, { 
    email,
    customMessage 
  });
  return response.data;
};

export const checkSmsConsent = async (phoneNumber) => {
  const response = await api.get(`/api/sessions/check-sms-consent/${encodeURIComponent(phoneNumber)}`);
  return response.data;
};

export const sendSessionSMS = async (sessionId, phoneNumber, messageType, customMessage = null, consentGiven = false) => {
  const response = await api.post(`/api/sessions/${sessionId}/send-sms`, { 
    phoneNumber, 
    messageType,
    customMessage,
    consentGiven: consentGiven ? 'true' : 'false'
  });
  return response.data;
};

export const uploadPhoto = async (roomId, photoBlob, latitude = null, longitude = null, accuracy = null) => {
  const formData = new FormData();
  formData.append('photo', photoBlob, 'snapshot.jpg');
  if (latitude != null) formData.append('latitude', latitude);
  if (longitude != null) formData.append('longitude', longitude);
  if (accuracy != null) formData.append('accuracy', accuracy);

  const response = await api.post(`/upload?roomId=${encodeURIComponent(roomId)}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Fetch an authenticated image and return a blob URL
export const fetchAuthenticatedImage = async (url) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    throw error;
  }
};

export default api;
